import { useMemo, memo } from "react";
import { parseChildMedia } from "../../rcds/rcdsRequest";
import appSettings from "../../settings/appSettings";
import { OrderData } from "../../types";
import { getFallbackChildData } from "../../helpers";

export const TourOrderConfirmComplete = (props) => {
  const { visible = true, childDetails, status } = props;
  const { orderData, sakConfig } = appSettings;
  const questionnaireEnabled = sakConfig?.questionnaireEnabled || false;
  const { line_items } = (orderData || {}) as OrderData;

  const fallbackChildData = getFallbackChildData(line_items);
  const {
    givenName: childName = fallbackChildData?.givenName || "your sponsored child",
    gender = ""
  } = childDetails;
  const possessiveAdjective =
    gender.toLowerCase() === "female" ? "her" : gender.toLowerCase() === "male" ? "his" : "their";

  const childPhotoUrl = useMemo(() => {
    if (!Array.isArray(childDetails?.childMedia)) {
      // attempt to get photo from fallback data
      if (Array.isArray(fallbackChildData?.childMedia)) {
        return fallbackChildData.childMedia[0].cgpUrl;
      }
      return "";
    }

    const childPhotos = parseChildMedia(childDetails);

    return childPhotos.cgpUrl;
  }, [childDetails]);

  if (!visible) {
    return null;
  }

  const ChildPhoto = memo((props: { url: string; name?: string }) => {
    const { url, name = "your sponsored child" } = props;
    if (!url) return null;
    return (
      <div className="child-photo__frame">
        <img className="child-photo__frame-image" src={url} alt={`Photo of ${name}`} />
      </div>
    );
  });

  function sendingYouBlock() {
    return (
      <div className="order-confirmation__sending-you">
        <h3 className="__staple--small __staple">Here&apos;s What We&apos;re Sending You:</h3>
        <ul>
          <li>A confirmation email and receipt</li>
          <li>A sponsorship welcome kit</li>
          <li>A letter from your sponsored child</li>
        </ul>
      </div>
    );
  }

  function whatHappensNextBlock() {
    return (
      <div className="order-confirmation__what-happens-next">
        <h3>What Happens Next?</h3>
        <ol>
          <li>
            <span>You’ll receive an email confirmation receipt shortly.</span>
          </li>
          <li>
            <span>A personalized sponsorship welcome kit will soon be on its way.</span>
          </li>
          <li>
            <span>
              {childName} will write you a message — check your email to see when it arrives!
            </span>
          </li>
        </ol>
      </div>
    );
  }

  if (status === "post-order-flow-completed" && questionnaireEnabled) {
    return (
      <>
        <div className="order-confirmation__confirm-complete sak">
          <div className="post-checkout-flow__header">
            <h3 className="post-checkout-flow__header-text __staple--small __staple">
              Thank you for showing Jesus&apos; love to {childName}!
            </h3>
          </div>
          <div className="post-checkout-flow__instructions">
            <p className="eta">
              Your photo and answers will be placed on a special card for {childName}. Watch for a
              response — something special to look forward to!
            </p>
          </div>
        </div>
        {whatHappensNextBlock()}
      </>
    );
  } else if (status === "post-order-flow-completed") {
    // Photo upload enabled but no SAK questionnaire; thus no SAK treatment
    return (
      <>
        <div className="order-confirmation__confirm-complete">
          <div className="order-confirmation__complete-text">
            <div className="title-text">
              <strong>Photo received! We'll make sure {childName} has your picture.</strong>
            </div>
          </div>
          <div className="order-confirmation__child-photo">
            <ChildPhoto url={childPhotoUrl} name={childName} />
          </div>
        </div>
        {whatHappensNextBlock()}
      </>
    );
  } else if (questionnaireEnabled) {
    // User skipped both photo upload and SAK questionnaire (status "photo-skipped")
    return (
      <>
        <div className="order-confirmation__confirm-complete sak">
          <div className="order-confirmation__child-photo">
            <ChildPhoto url={childPhotoUrl} name={childName} />
          </div>
          <div className="order-confirmation__complete-text">
            <h3 className="post-checkout-flow__header-text __staple--small __staple">
              Thank you for showing Jesus' love to {childName}!
            </h3>
            <p className="eta">
              We’ll let {childName} know you’re {possessiveAdjective} new sponsor! Watch for your
              welcome kit — something special to look forward to!
            </p>
          </div>
        </div>
        {whatHappensNextBlock()}
      </>
    );
  } else {
    // No photo upload or SAK questionnaire
    return (
      <div className="order-confirmation__confirm-complete">
        <div className="order-confirmation__complete-text">
          <div className="title-text">
            <strong>Payment confirmed!</strong>
          </div>
          <p>
            We'll notify <strong>{childName}</strong> that you're {possessiveAdjective} new sponsor!
          </p>
        </div>
        <div className="order-confirmation__child-photo">
          <ChildPhoto url={childPhotoUrl} name={childName} />
        </div>
        {sendingYouBlock()}
      </div>
    );
  }
};

export default TourOrderConfirmComplete;
